import React, { createContext, useContext, useEffect, useState } from 'react';
import Router, { useRouter } from 'next/router';
import { Hub } from '@aws-amplify/core';
import Auth from '@aws-amplify/auth';
import { v4 as uuidv4 } from 'uuid';
import * as ls from 'local-storage';
import { AccessLevel } from '../libs/analytics';
import { LogDispatchContext } from './logContext';

type AuthUserType = {
  id: string;
  fName: string;
  lName: string;
  email: string;
  accessLevel: string;
};
type AuthContextType = {
  authenticating: boolean;
  isAuthenticated: boolean;
  hasCognitoSession: boolean;
  setUserSession: any;
  getUser: any;
  user: AuthUserType | undefined;
  signout: () => void;
  getUserFullName: () => string;
  clearSession: () => void;
};
type AuthContextProps = {
  children: React.ReactNode;
};

export const AuthContext = createContext({} as AuthContextType);

export function AuthProvider({ children }: AuthContextProps): any {
  const { log } = useContext(LogDispatchContext);
  const [user, setUser] = useState(undefined);
  const [authenticating, setAuthenticating] = useState(true);
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [hasCognitoSession, setHasCognitoSession] = useState(false);
  const router = useRouter();
  const { pathname } = useRouter();

  function getSlug() {
    let path: any = router.asPath.split('#');
    path = path.length > 1 ? path[1] : path[0];

    const [, conferenceSlug, eventSlug, additionalSlug] = path.split('/');

    if (conferenceSlug === 'login?page=') {
      return `${eventSlug}-${additionalSlug}`;
    }
    return `${conferenceSlug}-${eventSlug}`;
  }

  function redirectPage() {
    // Make sure we're in the browser
    if (typeof window !== 'undefined') {
      if (
        router.pathname !== '/login' &&
        router.pathname !== '/register' &&
        router.pathname !== '/signOut' &&
        router.pathname !== '/forgot-password'
      ) {
        Router.push('/login');
      }
    }
  }

  function sanitizeUser(data: any): AuthUserType {
    log.debug({
      source: 'AuthContext',
      action: 'sanitizeUser',
      data
    });

    if (!data) {
      redirectPage();
    }

    return {
      id: data.id,
      fName: data.fName,
      lName: data.lName,
      email: data.email,
      accessLevel: data.accessLevel
    };
  }

  function getUser(cache: boolean = true) {
    const tUser = cache ? user : sanitizeUser(ls.get<any>(getSlug()));
    return { ...tUser };
  }

  async function setUserSession(fName: string, lName: string, email: string, accessLevel: AccessLevel, id?: string) {
    const lsEventId = ls.get<any>(getSlug());
    const uuid = id || (lsEventId && lsEventId.id) || uuidv4();

    const tUser = { id: uuid, fName, lName, email, accessLevel };
    ls.set<any>(getSlug(), tUser);

    setUser(sanitizeUser(tUser) as any);
    setAuthenticated(true);
    setAuthenticating(false);
    return uuid;
  }

  function refreshSession() {
    setAuthenticating(true);
    // Fetch user info from local storage.
    const lsEventId = ls.get<any>(getSlug());

    if (lsEventId && lsEventId.accessLevel !== AccessLevel.OPEN) {
      const tUser = sanitizeUser({
        id: lsEventId && lsEventId.id,
        fName: lsEventId && lsEventId.fName,
        lName: lsEventId && lsEventId.lName,
        email: lsEventId && lsEventId.email,
        accessLevel: lsEventId && lsEventId.accessLevel
      });
      setUser(tUser);
    } else if (!lsEventId) {
      const uuid = uuidv4();
      const tUser = sanitizeUser({
        id: uuid,
        fName: null,
        lName: null,
        email: null,
        accessLevel: AccessLevel.OPEN
      });
      ls.set<any>(getSlug(), tUser);

      setUser(tUser);
    }

    setAuthenticated(true);
    setAuthenticating(false);
  }

  async function clearSession() {
    await Auth.signOut();
    ls.clear();
    setUser(undefined as any);
    setAuthenticated(false);
    setAuthenticating(false);
  }

  function signout() {
    clearSession();
  }

  function getUserName() {
    if (!user) {
      setAuthenticating(true);
      // Fetch user info from local storage.
      const lsEventId = ls.get<any>(getSlug());
      setUser(
        sanitizeUser({
          id: lsEventId && lsEventId.id,
          fName: lsEventId && lsEventId.fName,
          lName: lsEventId && lsEventId.lName,
          email: lsEventId && lsEventId.email,
          accessLevel: lsEventId && lsEventId.accessLevel
        }) as any
      );
      setAuthenticated(true);
      setAuthenticating(false);

      return lsEventId && lsEventId.fName;
    }

    return user && user.fName;
  }

  function getUserFullName() {
    if (!user) {
      setAuthenticating(true);
      // Fetch user info from local storage.
      const lsEventId = ls.get<any>(getSlug());
      setUser(
        sanitizeUser({
          id: lsEventId && lsEventId.id,
          fName: lsEventId && lsEventId.fName,
          lName: lsEventId && lsEventId.lName,
          email: lsEventId && lsEventId.email,
          accessLevel: lsEventId && lsEventId.accessLevel
        }) as any
      );
      setAuthenticated(true);
      setAuthenticating(false);

      return lsEventId && lsEventId.fName;
    }

    return user && `${user.fName} ${user.lName}`;
  }

  function notAuthenticated() {
    if (!isAuthenticated) {
      refreshSession();
    }
  }

  useEffect(() => {
    refreshSession();
    // eslint-disable-next-line
  }, []);

  // We check the user’s auth with every route change.
  useEffect(() => {
    notAuthenticated();
  }, [pathname]);

  // Set authentication on sign-in / sign-out events
  useEffect(() => {
    Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signIn') {
        setHasCognitoSession(true);
      }

      if (payload.event === 'signOut') {
        setHasCognitoSession(false);
      }
    });

    // Check for initial cognito session
    Auth.currentSession()
      .then((data) => {
        setHasCognitoSession(true);
      })
      .catch((err) => {
        setHasCognitoSession(false);
      });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        authenticating,
        isAuthenticated,
        user,
        hasCognitoSession,
        setUserSession,
        getUser,
        getUserFullName,
        clearSession,
        signout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const withAuthContext = (Component: any) => (props: any) => (
  <AuthContext.Consumer>{(value) => <Component authContext={value} {...props} />}</AuthContext.Consumer>
);
